/**
 * Portal status codes.
 *
 * Reproduced verbatim from RinNET_frontend's `src/lib/models.ts`. This file IS the
 * compatibility contract: the frontend switches on these numbers, so a value changed
 * here silently changes what the UI does.
 *
 * They are banded by domain — card 1xxxx, user 2xxxx, auth/oauth/TOTP 3xxxx, rival
 * 4xxxx, global 9xxxx — and deliberately distinct from HTTP status. The mapping
 * between the two is in `http.ts` and is load-bearing in ways the client never
 * documents.
 */
export const StatusCode = {
  // Card, 10000-19999
  CARD_NOT_FOUND: 14041,
  REGISTER_NEW_CARD_SUCCESS: 12001,
  REGISTER_NEW_CARD_ERROR: 15001,
  SET_DEFAULT_CARD_SUCCESS: 12002,
  SET_DEFAULT_CARD_ERROR: 15002,
  REMOVE_CARD_EXTERNAL_SUCCESS: 12003,
  REMOVE_CARD_EXTERNAL_ERROR: 15003,
  CARD_EXTERNAL_NOT_FOUND: 14042,
  CARD_ALREADY_LINKED_BY_OTHERS: 14091,
  CARD_ALREADY_LINKED_BY_YOU: 14092,
  CARD_NOT_BELONG_TO_YOU: 94031,
  BIND_CARD_SUCCESS: 12004,
  BIND_CARD_ERROR: 15004,
  UNBIND_CARD_SUCCESS: 12005,
  UNBIND_CARD_ERROR: 15005,
  INVALID_ACCESS_CODE: 14001,
  ADD_ACCESS_CODE_SUCCESS: 12006,
  ADD_ACCESS_CODE_ERROR: 15006,
  CHANGE_ACCESS_CODE_SUCCESS: 12007,
  CHANGE_ACCESS_CODE_ERROR: 15007,

  // User, 20000-29999
  USER_NOT_FOUND: 24041,
  USER_FETCH_SUCCESS: 22001,

  // Auth, 30000-39999
  LOGIN_SUCCESS: 32001,
  LOGIN_FAILED: 34011,
  USERNAME_ALREADY_TAKEN: 34091,
  EMAIL_ALREADY_IN_USE: 34092,
  VERIFY_CODE_NOT_CORRECT: 34001,
  AUTH_INVALID_CREDENTIALS: 34011,
  USER_REGISTER_SUCCESS: 32011,
  VERIFY_CODE_SEND_SUCCESS: 32002,
  VERIFY_CODE_SEND_ERROR: 35001,
  /** Rate limited. The `3429x` band derives HTTP 429 with no table entry. */
  TOO_MANY_REQUESTS: 34290,
  VERIFY_CODE_SEND_TOO_FAST: 34291,
  RESET_PASSWORD_SUCCESS: 32003,
  RESET_PASSWORD_ERROR: 35002,
  RESET_PASSWORD_FAILED: 34002,

  // TOTP and account state
  TOTP_REQUIRED: 34014,
  TOTP_INVALID: 34015,
  TOTP_ALREADY_ENABLED: 34006,
  TOTP_NOT_ENABLED: 34007,
  TOTP_TOO_MANY_ATTEMPTS: 34292,
  PASSWORD_INCORRECT: 34016,
  ACCOUNT_BANNED: 34031,
  ACCOUNT_BAN_FORBIDDEN: 34032,
  EULA_REQUIRED: 34033,
  /** Signed in, but every route except changing the password is closed. */
  PASSWORD_CHANGE_REQUIRED: 34034,
  EULA_VERSION_INVALID: 34093,
  GAME_PROFILE_NOT_FOUND: 34044,
  GAME_PROFILE_STATE_INVALID: 34008,
  GAME_SAVE_DELETE_CONFIRMATION_INVALID: 34009,
  AUTH_CREDENTIAL_NOT_FOUND: 34045,
  EULA_DRAFT_NOT_FOUND: 34046,

  // OAuth
  OAUTH_INVALID_CREDENTIALS: 34013,
  OAUTH_USER_NOT_REGISTERED: 34042,
  OAUTH_ALREADY_REGISTERED: 34005,

  // Rival, 40000-49999
  RIVAL_NOTFOUND: 44041,
  RIVAL_SELF: 44001,
  RIVAL_LIMIT_REACHED: 44002,
  RIVAL_ALREADY_ADDED: 44003,

  // Global, 90000-99999
  OK: 92001,
  UNAUTHORIZED: 94011,
  BAD_REQUEST: 94001,
  NOT_FOUND: 94041,
  METHOD_NOT_ALLOWED: 94051,
  INTERNAL_SERVER_ERROR: 95001,
} as const;

export type StatusCode = (typeof StatusCode)[keyof typeof StatusCode];

/** Human-readable text for a code. Shown directly by the frontend on failure. */
export const STATUS_MESSAGE: Readonly<Record<number, string>> = {
  [StatusCode.OK]: 'OK',
  [StatusCode.UNAUTHORIZED]: 'Insufficient authentication',
  [StatusCode.BAD_REQUEST]: 'Bad request',
  [StatusCode.NOT_FOUND]: 'Not found',
  [StatusCode.METHOD_NOT_ALLOWED]: 'Method not allowed',
  [StatusCode.INTERNAL_SERVER_ERROR]: 'Internal server error',

  [StatusCode.LOGIN_SUCCESS]: 'Login successful',
  [StatusCode.AUTH_INVALID_CREDENTIALS]: 'Incorrect username or password',
  [StatusCode.USERNAME_ALREADY_TAKEN]: 'That username is taken',
  [StatusCode.EMAIL_ALREADY_IN_USE]: 'That email address is already in use',
  [StatusCode.USER_REGISTER_SUCCESS]: 'Registration successful',
  [StatusCode.VERIFY_CODE_NOT_CORRECT]: 'That verification code is not correct',
  [StatusCode.TOO_MANY_REQUESTS]: 'Too many attempts. Please wait and try again',
  [StatusCode.VERIFY_CODE_SEND_TOO_FAST]: 'Please wait before requesting another code',
  [StatusCode.RESET_PASSWORD_SUCCESS]: 'Password reset',
  [StatusCode.RESET_PASSWORD_FAILED]: 'Could not reset the password',

  [StatusCode.TOTP_REQUIRED]: 'Two-factor authentication required',
  [StatusCode.TOTP_INVALID]: 'That two-factor code is not correct',
  [StatusCode.TOTP_TOO_MANY_ATTEMPTS]: 'Too many attempts; try again later',
  [StatusCode.PASSWORD_INCORRECT]: 'Incorrect password',
  [StatusCode.ACCOUNT_BANNED]: 'This account is banned',
  [StatusCode.EULA_REQUIRED]: 'You must accept the terms of use',
  [StatusCode.PASSWORD_CHANGE_REQUIRED]: 'You must change your password before continuing',

  [StatusCode.USER_NOT_FOUND]: 'No such user',
  [StatusCode.USER_FETCH_SUCCESS]: 'OK',

  [StatusCode.CARD_NOT_FOUND]: 'No such card',
  [StatusCode.CARD_NOT_BELONG_TO_YOU]: 'That card is not yours',
  [StatusCode.CARD_ALREADY_LINKED_BY_OTHERS]: 'That card is already linked to another account',
  [StatusCode.CARD_ALREADY_LINKED_BY_YOU]: 'That card is already linked to your account',
  [StatusCode.INVALID_ACCESS_CODE]: 'That access code is not valid',
  [StatusCode.BIND_CARD_SUCCESS]: 'Card linked',
  [StatusCode.UNBIND_CARD_SUCCESS]: 'Card unlinked',
  [StatusCode.SET_DEFAULT_CARD_SUCCESS]: 'Default card changed',

  [StatusCode.GAME_PROFILE_NOT_FOUND]: 'No game profile for that card',
  [StatusCode.RIVAL_NOTFOUND]: 'No such rival',
  [StatusCode.RIVAL_SELF]: 'You cannot rival yourself',
  [StatusCode.RIVAL_LIMIT_REACHED]: 'Rival list is full',
  [StatusCode.RIVAL_ALREADY_ADDED]: 'Already a rival',
};

export function statusMessage(code: number): string {
  return STATUS_MESSAGE[code] ?? 'Unknown status';
}
